<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        Performance Score Setup
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <DatePicker
          v-model="query.date"
          type="month" />
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          @click="getItems()">
          Search
        </v-btn>
        <v-btn
          color="secondary"
          rounded
          small
          :loading="loading"
          @click="onShowCopyDialog()">
          Copy
        </v-btn>
      </div>
    </div>

    <v-row class="px-8">
      <v-col
        cols="12"
        class="card-container elevation-3">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="-1"
          :loading="loading"
          hide-default-footer>
          <template #[`item.warehouse.name`]="{ item }">
            <v-btn
              color="secondary"
              small
              text
              :to="`/pms/performance-score-setup/${item.warehouse.id}?date=${query.date}`">
              <span class="text-decoration-underline">
                {{ item.warehouse.name }}
              </span>
            </v-btn>
          </template>
          <template #[`item.status`]="{ item, index }">
            <v-switch
              v-model="items[index].status"
              class="ma-0"
              false-value="inactive"
              true-value="active"
              color="secondary"
              inset
              dense
              hide-details
              :disabled="loading || !item.id"
              @change="updateStatusItem(item.id, $event)" />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog
      v-model="dialog"
      max-width="500px"
      persistent>
      <v-card>
        <v-card-title>
          <h3>
            Copy Score Setup
          </h3>
          <span class="grey--text text-caption">
            Please select target month to paste this setting.
          </span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="copy-form"
              @submit.prevent="onConfirmCopy()">
              <v-row>
                <v-col cols="12">
                  <DatePicker
                    v-model="targetDate"
                    type="month"
                    :min="minMonth"
                    color="secondary"
                    outlined />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            :disabled="cloning"
            @click="onClose()">
            Cancel
          </v-btn>
          <v-btn
            :loading="cloning"
            color="success"
            text
            @click="onConfirmCopy()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DatePicker from '@/components/DatePicker.vue'
import PerformanceScoresProvider from '@/resources/PerformanceScoresProvider'
import EvaluationTopics from '@/assets/json/EvaluationTopics.json'

const PerformanceScoresService = new PerformanceScoresProvider()

export default {
  components: {
    DatePicker
  },
  data () {
    return {
      rawItems: [],
      items: [],
      query: {
        date: this.$dayjs().format('YYYY-MM')
      },
      mainTopics: EvaluationTopics,
      defaultValue: {
        id: null,
        month: this.$dayjs().month() + 1,
        year: this.$dayjs().year(),
        warehouse: null,
        topics: [
          {
            topicType: '',
            operator: 'greater_than_or_equal',
            conditionType: 'percent',
            target: 0,
            weight: 0,
            available: false
          }
        ],
        status: 'inactive'
      },
      loading: false,
      dialog: false,
      cloning: false,
      targetDate: this.$dayjs().add(1, 'month').format('YYYY-MM'),
      minMonth: this.$dayjs().add(1, 'month').format('YYYY-MM')
    }
  },
  computed: {
    warehouses () {
      return this.$store.getters['Store/warehouse']
        ?.filter((warehouse) => warehouse.id !== 0 && warehouse.type === 'store' && warehouse.status === 'active')
        ?.sort((a, b) => a.name.localeCompare(b.name)) || []
    },
    headers () {
      return [
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '60px',
          class: 'sticky-first',
          cellClass: 'sticky-first'
        },
        {
          text: 'Branch',
          value: 'warehouse.name',
          sortable: false,
          width: '240px',
          class: 'sticky-second sticky-border',
          cellClass: 'sticky-second sticky-border'
        },
        ...this.mainTopics.map((topic) => ({
          text: topic.name,
          value: topic.id,
          sortable: false,
          width: '110px',
          align: 'center',
          class: topic?.provider ? `${topic.provider}-color` : ''
        }))
      ]
    }
  },
  watch: {
    'query.date': {
      handler () {
        this.getItems()
      },
      deep: true
    }
  },
  mounted () {
    this.getItems()
  },
  methods: {
    async getItems () {
      try {
        this.loading = true

        const { data } = await PerformanceScoresService.getItems({
          startDate: this.$dayjs(this.query.date)
                      .startOf('month')
                      .hour(0)
                      .minute(0)
                      .second(0)
                      .utc()
                      .toISOString(),
          endDate: this.$dayjs(this.query.date)
                      .endOf('month')
                      .hour(23)
                      .minute(59)
                      .second(59)
                      .utc()
                      .toISOString()
        })

        this.rawItems = data
        this.items = this.warehouses.map((warehouse) => {
          const found = data.find((v) => v.warehouse.id === warehouse.id)
          const topics = found?.topics || []
          const mappedTopics = this.mainTopics.reduce((acc, topic) => {
            acc[topic.id] = topics.find((v) => v.topicType === topic.id && v?.available)?.weight ?? '-'

            return acc
          }, {})

          if (found) {
            return {
              ...found,
              ...mappedTopics
            }
          }

          return {
            ...this.defaultValue,
            warehouse: {
              id: warehouse.id,
              name: warehouse.name,
              code: warehouse.code
            },
            ...mappedTopics
          }
        })
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async updateStatusItem (id, status) {
      if (this.loading) {
        return
      }

      try {
        this.loading = true

        await PerformanceScoresService.updateStatusItem(id, { status })

        this.getItems()
      } catch (error) {
        console.error('updateStatusItem', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    onShowCopyDialog () {
      this.targetDate = this.$dayjs().add(1, 'month').format('YYYY-MM')
      this.dialog = true
    },
    async onConfirmCopy () {
      const isValid = await this.$refs['copy-form'].validate()

      if (!isValid) {
        return
      }

      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Copy Item',
        message: 'Are you want to copy this setting?',
        confirmText: 'Copy',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.onCopy()
      })
    },
    async onCopy () {
      try {
        this.cloning = true

        const year = Number(this.targetDate.split('-')[0])
        const month = Number(this.targetDate.split('-')[1])

        await Promise.all(this.rawItems.map((item) => PerformanceScoresService.createItem({
          ...item,
          month,
          year
        })))

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Copy setup successfully.',
          type: 'success'
        })

        this.onClose()
      } catch (error) {
        console.error('onCopy', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.cloning = false
      }
    },
    onClose () {
      this.dialog = false
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-second,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.sticky-second {
  position: sticky;
  background: white;
  z-index: 2;
}

::v-deep .sticky-first {
  left: 0;
}
::v-deep .sticky-second {
  left: 88px;
}
::v-deep .sticky-border {
  border-right: 1px solid #e0e0e0;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-second {
  z-index: 3;
}
</style>

<style>
.palexy-color span {
  color: #00BFFF !important;
}

.gentlewoman-color span {
  color: #E20712 !important;
}
</style>
